import './styles/main.css';
import { CONFIG, isConfigured } from './config';
import { applyTheme } from './ui/theme';
import { readStateFromLocation, type DriveState } from './drive/state';
import { setUserHint, getToken, AuthError } from './auth/gis';
import { createFile, getMetadata, renameFile } from './drive/api';
import { DocumentStore } from './store/document';
import { Shell } from './ui/shell';
import { showModal, promptText } from './ui/modal';
import type { EditorView } from '@codemirror/view';

const root = document.getElementById('app')!;
const store = new DocumentStore();
let shell: Shell;
let editor: EditorView | null = null;
let conflictOpen = false;

async function main(): Promise<void> {
  const state = readStateFromLocation();
  if (!state) {
    renderHome();
    return;
  }
  setUserHint(state.userId);

  shell = new Shell(root, {
    onViewChange: (mode) => {
      if (mode === 'edit') void mountEditor();
      else void shell.rerenderPreview(false, store.get().content);
    },
    onSave: () => void store.save(),
    onRename: () => void rename(),
  });

  store.subscribe((s) => {
    shell.update(s);
    if (s.saveState === 'conflict') void handleConflict();
    if (s.saveState === 'error' && s.error) {
      shell.banner('save-error', s.error, 'error', {
        label: 'Újra',
        run: () => void store.save(),
      });
    } else {
      shell.dismissBanner('save-error');
    }
  });

  window.addEventListener('beforeunload', (e) => {
    if (store.isDirty()) {
      e.preventDefault();
      e.returnValue = '';
    }
  });

  try {
    await getToken(false);
  } catch {
    // Csendes hitelesítés nem ment – felhasználói gesztus kell hozzá.
    await waitForUserGesture();
  }

  try {
    if (state.action === 'open') await openFile(state);
    else await createNewFile(state);
  } catch (err) {
    reportFatal(err);
  }
}

/** Ha a csendes token-kérés nem megy (pl. első használat), gombra várunk. */
function waitForUserGesture(): Promise<void> {
  return new Promise((resolve) => {
    shell.banner(
      'auth',
      'Kapcsolódás a Google Drive-hoz szükséges a fájl megnyitásához.',
      'info',
      {
        label: 'Kapcsolódás',
        run: async () => {
          try {
            await getToken(true);
            shell.dismissBanner('auth');
            resolve();
          } catch (err) {
            shell.banner('auth', authMessage(err), 'error', {
              label: 'Újra',
              run: () => void getToken(true).then(() => (shell.dismissBanner('auth'), resolve())),
            });
          }
        },
      },
    );
  });
}

async function openFile(state: Extract<DriveState, { action: 'open' }>): Promise<void> {
  const { draft } = await store.load(state.fileId, state.resourceKey);
  await shell.rerenderPreview(true, store.get().content);

  if (draft) {
    const answer = await showModal<'restore' | 'discard'>({
      title: 'Mentetlen piszkozat',
      body:
        'Ezen a gépen található egy korábbi, el nem mentett változat erről a fájlról. ' +
        'Visszaállítod, vagy a Drive-ban lévő verzióval dolgozol tovább?',
      actions: [
        { label: 'Piszkozat visszaállítása', value: 'restore', variant: 'primary' },
        { label: 'Drive verziója', value: 'discard' },
      ],
    });
    if (answer === 'restore') {
      store.update(draft);
      setEditorDoc(draft);
      shell.setMode('edit');
      return;
    }
    localStorage.removeItem(`bitsquad-md:draft:${state.fileId}`);
  }

  if (store.get().readOnly) {
    shell.banner('readonly', 'Ehhez a fájlhoz nincs szerkesztési jogod — csak olvasható.', 'warn');
  }
  if (Number(store.get().meta?.size ?? 0) > CONFIG.largeFileBytes) {
    shell.banner('large', 'Nagy fájl — a szerkesztő lassabb lehet.', 'warn');
  }
}

async function createNewFile(state: Extract<DriveState, { action: 'create' }>): Promise<void> {
  // Megnézzük, hova fog kerülni a fájl, és megmutatjuk a párbeszédben.
  let location = 'Saját meghajtó (gyökér)';
  if (state.folderId) {
    try {
      const folder = await getMetadata(state.folderId, state.folderResourceKey);
      location = folder.driveId ? `${folder.name} (közös meghajtó)` : folder.name;
    } catch {
      location = 'a Drive-ban megnyitott mappa';
    }
  }

  const name = await promptText({
    title: 'Új markdown dokumentum',
    label: `Fájlnév — ide kerül: ${location}`,
    value: 'Névtelen dokumentum.md',
    confirmLabel: 'Létrehozás',
  });
  if (!name) {
    shell.fatal('Nem jött létre fájl', 'Bezárhatod ezt a fület.');
    return;
  }

  const fileName = /\.(md|markdown|mdx)$/i.test(name) ? name : `${name}.md`;
  const initial = `# ${fileName.replace(/\.(md|markdown|mdx)$/i, '')}\n\n`;
  const { meta, fellBackToRoot, reason } = await createFile(
    fileName,
    initial,
    state.folderId,
    state.folderResourceKey,
  );

  store.adopt(meta, initial);
  await shell.rerenderPreview(true, initial);
  shell.setMode('edit');

  if (fellBackToRoot) {
    shell.banner(
      'create-location',
      `A fájl a Saját meghajtó gyökerébe került, nem a megnyitott mappába. ${reason ?? ''}`.trim(),
      'warn',
      meta.webViewLink
        ? { label: 'Megnyitás a Drive-ban', run: () => window.open(meta.webViewLink, '_blank') }
        : undefined,
    );
  }
}

let editorLoading = false;

/** A szerkesztő (CodeMirror) csak az első szerkesztésnél töltődik le. */
async function mountEditor(): Promise<void> {
  if (editor) {
    editor.focus();
    return;
  }
  if (editorLoading) return;
  editorLoading = true;
  const { createEditor } = await import('./editor/editor');
  editorLoading = false;
  editor = createEditor(
    shell.editorHost,
    store.get().content,
    {
      onChange: (doc) => store.update(doc),
      onSave: () => void store.save(),
      onToggleView: () => shell.setMode('read'),
      onCursor: (line, col) => shell.setCursor(line, col),
    },
    store.get().readOnly,
  );
  editor.focus();
}

function setEditorDoc(text: string): void {
  if (!editor) return;
  editor.dispatch({ changes: { from: 0, to: editor.state.doc.length, insert: text } });
}

async function handleConflict(): Promise<void> {
  if (conflictOpen) return;
  conflictOpen = true;
  const answer = await showModal<'overwrite' | 'copy' | 'reload'>({
    title: 'A fájlt időközben más módosította',
    body:
      'A Drive-ban lévő verzió újabb, mint amit megnyitottál. Válaszd ki, mi történjen. ' +
      '(A Drive verziótörténetében minden korábbi változat megmarad.)',
    actions: [
      { label: 'Az én verzióm mentése', value: 'overwrite', variant: 'danger' },
      { label: 'Mentés másolatként', value: 'copy', variant: 'primary' },
      { label: 'Drive verziójának betöltése', value: 'reload' },
    ],
  });
  conflictOpen = false;

  if (answer === 'overwrite') await store.overwrite();
  else if (answer === 'copy') {
    const copy = await store.saveCopy();
    if (copy) {
      shell.banner('copy', `Mentve új fájlba: ${copy.name}`, 'info');
    }
  } else if (answer === 'reload') {
    await store.reloadFromDrive();
    setEditorDoc(store.get().content);
    await shell.rerenderPreview(true, store.get().content);
  }
}

async function rename(): Promise<void> {
  const meta = store.get().meta;
  if (!meta || store.get().readOnly) return;
  const name = await promptText({
    title: 'Fájl átnevezése',
    label: 'Új név',
    value: meta.name,
    confirmLabel: 'Átnevezés',
  });
  if (!name || name === meta.name) return;
  const updated = await renameFile(meta.id, name);
  store.adopt({ ...meta, ...updated }, store.get().content);
}

function authMessage(err: unknown): string {
  if (err instanceof AuthError) return err.message;
  return 'Nem sikerült kapcsolódni a Google fiókodhoz.';
}

function reportFatal(err: unknown): void {
  const detail = err instanceof Error ? err.message : String(err);
  shell.fatal('A fájl nem nyitható meg', detail);
}

/**
 * Ha valaki csak beírja az URL-t (nincs `state` paraméter): kezdőoldal.
 * Egyben telepítési önellenőrzés — megmutatja, be van-e kötve a Google projekt.
 */
function renderHome(): void {
  const configured = isConfigured();
  applyTheme();
  root.innerHTML = `
    <div class="home">
      <div class="home__card">
        <div class="home__brand"><span class="home__logo" aria-hidden="true">◧</span> Bitsquad MD</div>
        <h1>Markdown a Google Drive-ban, olvashatóan.</h1>
        <p class="home__lead">
          Ez az oldal önmagában nem csinál semmit — a Drive-ból nyílik meg, azzal a fájllal,
          amelyikre rákattintasz.
        </p>

        <ol class="home__steps">
          <li><strong>Olvasás vagy szerkesztés:</strong> a Drive-ban kattints egy
            <code>.md</code> fájlra, vagy jobb klikk →
            <em>Megnyitás ezzel</em> → <em>Bitsquad MD</em>.</li>
          <li><strong>Új dokumentum:</strong> a Drive-ban <em>Új</em> →
            <em>Bitsquad Markdown</em>.</li>
          <li><strong>Mentés:</strong> automatikus, illetve <kbd>Ctrl</kbd>+<kbd>S</kbd>.
            A fájl végig a Drive-ban marad.</li>
        </ol>

        <div class="home__actions">
          <a class="btn btn--primary" href="https://drive.google.com" target="_blank" rel="noopener">Ugrás a Drive-ba</a>
          <a class="btn" href="/support.html">Súgó</a>
          <a class="btn" href="/privacy.html">Adatkezelés</a>
        </div>

        <div class="statuscard statuscard--${configured ? 'ok' : 'pending'}">
          <span class="statuscard__dot" aria-hidden="true"></span>
          <div>
            <p class="statuscard__title">${
              configured
                ? 'Az alkalmazás össze van kötve a Google projekttel.'
                : 'Még nincs beállítva a Google Client ID.'
            }</p>
            <p class="statuscard__detail">${
              configured
                ? 'A Drive-integráció készen áll. Ha a Drive menüjében még nem látszik az app, az admin telepítés hiányzik.'
                : 'A szerveren nyisd meg a <code>config.js</code> fájlt, és írd be a <code>clientId</code> értékét. Újrabuildelni nem kell, elég frissíteni az oldalt.'
            }</p>
          </div>
        </div>

        <p class="home__foot">${CONFIG.appName} · build: ${BUILD_TIME} · ${location.host}</p>
      </div>
    </div>
  `;
}

void main();
